import { api, ApiError, ForbiddenError, NetworkError, NotFoundError } from "./api";

/*
 * «Мой график» — личные события учётки, `/api/v1/schedule/events` бэкенда
 * (`sc_atyrau_backend/docs/schedule-api.md`). Право — `schedule.own.use`.
 *
 * ПРАВИЛА ФОРМЫ ПРОВЕРЯЮТСЯ И ЗДЕСЬ, ТЕМИ ЖЕ КОДАМИ, ЧТО У СЕРВЕРА. Ошибка
 * встаёт под форму до запроса, а если сервер всё же отобьёт — тем же `code`,
 * и экран показывает одно и то же сообщение в обоих случаях.
 */

/** Событие графика — ровно модель контракта. Дата и время местные, строками. */
export type ScheduleEvent = {
  id: number;
  /** `YYYY-MM-DD` */
  date: string;
  /** `HH:mm` */
  startTime: string;
  endTime: string | null;
  title: string;
  location: string | null;
  note: string | null;
  important: boolean;
  createdAt: string;
  updatedAt: string;
};

export type ScheduleEventInput = {
  date: string;
  startTime: string;
  endTime?: string | null;
  title: string;
  location?: string | null;
  note?: string | null;
  important?: boolean;
};

export type ScheduleEventPatch = Partial<ScheduleEventInput>;

export const SCHEDULE_TITLE_MAX = 500;
export const SCHEDULE_LOCATION_MAX = 255;
export const SCHEDULE_NOTE_MAX = 2000;
/** Шире не отдаёт сервер (`invalid_date_range`). */
export const SCHEDULE_MAX_RANGE_DAYS = 62;

/* ── Проверка — одна на оба режима ──────────────────────────────────── */

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

function isRealDate(value: string): boolean {
  if (!DATE_RE.test(value)) return false;
  const [y, m, d] = value.split("-").map(Number);
  const probe = new Date(y, m - 1, d);
  // `new Date(2026, 1, 31)` молча становится 3 марта — ловим это сравнением.
  return probe.getFullYear() === y && probe.getMonth() === m - 1 && probe.getDate() === d;
}

function emptyToNull(value: string | null | undefined): string | null {
  const trimmed = (value ?? "").trim();
  return trimmed ? trimmed : null;
}

function invalid(code: string, message: string): ApiError {
  return new ApiError(message, 400, code);
}

/** Событие в том виде, в каком его сохранит сервер, — или ошибка с `code` контракта. */
export function normalizeScheduleInput(input: ScheduleEventInput): Omit<
  ScheduleEvent,
  "id" | "createdAt" | "updatedAt"
> {
  const title = input.title.trim();
  if (!title) throw invalid("bad_request", "Укажите название события");
  if (title.length > SCHEDULE_TITLE_MAX)
    throw invalid("bad_request", `Название длиннее ${SCHEDULE_TITLE_MAX} символов`);
  if (!isRealDate(input.date)) throw invalid("bad_request", "Неверная дата");
  if (!TIME_RE.test(input.startTime)) throw invalid("bad_request", "Неверное время начала");

  const endTime = emptyToNull(input.endTime);
  if (endTime !== null) {
    if (!TIME_RE.test(endTime)) throw invalid("bad_request", "Неверное время окончания");
    // `HH:mm` с ведущими нулями сравнивается строкой так же, как числом.
    if (endTime <= input.startTime)
      throw invalid("invalid_time_range", "Окончание должно быть позже начала");
  }

  const location = emptyToNull(input.location);
  if (location && location.length > SCHEDULE_LOCATION_MAX)
    throw invalid("bad_request", `Место длиннее ${SCHEDULE_LOCATION_MAX} символов`);
  const note = emptyToNull(input.note);
  if (note && note.length > SCHEDULE_NOTE_MAX)
    throw invalid("bad_request", `Заметка длиннее ${SCHEDULE_NOTE_MAX} символов`);

  return {
    date: input.date,
    startTime: input.startTime,
    endTime,
    title,
    location,
    note,
    important: input.important ?? false,
  };
}

function daysBetween(from: string, to: string): number {
  const [fy, fm, fd] = from.split("-").map(Number);
  const [ty, tm, td] = to.split("-").map(Number);
  // UTC, а не местная полночь: в день перевода часов местные сутки не равны 24 ч.
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000);
}

function checkRange(from: string, to: string): void {
  if (!isRealDate(from) || !isRealDate(to) || from > to)
    throw invalid("invalid_date_range", "Неверный период");
  if (daysBetween(from, to) + 1 > SCHEDULE_MAX_RANGE_DAYS)
    throw invalid("invalid_date_range", `Период шире ${SCHEDULE_MAX_RANGE_DAYS} дней`);
}


/* ── Сервер: `/schedule/events` ─────────────────────────────────────── */

/*
 * `skipCache` на чтении обязателен: `apiRequest` держит GET в памяти, и без
 * него только что добавленное событие не появлялось бы до истечения кэша.
 */
/*
 * Сервер лежит или не достучаться: прокси отвечает 502/504 страницей без JSON,
 * и общий слой отдаёт «Неизвестная ошибка». Человеку нужно другое — что дело в
 * связи, данные целы и можно повторить.
 */
function explainOutage(err: unknown): never {
  // Права приходят только через роль должности — сам человек тут ничего не
  // исправит, поэтому и «Повторить» ему не предлагаем (`code` — для экрана).
  if (err instanceof ForbiddenError)
    throw new ApiError("У вашей учётки нет доступа к графику. Обратитесь к администратору.", 403, "forbidden");
  if (err instanceof NetworkError || (err instanceof ApiError && err.status >= 500))
    throw new ApiError("Сервер графика не отвечает. Проверьте связь и повторите.", err instanceof ApiError ? err.status : 0, "unavailable");
  throw err;
}

const remote = {
  list: (from: string, to: string) =>
    api
      .get<ScheduleEvent[]>("/schedule/events", { params: { from, to }, skipCache: true })
      .catch(explainOutage),
  create: (input: ScheduleEventInput) =>
    api.post<ScheduleEvent>("/schedule/events", normalizeScheduleInput(input)).catch(explainOutage),
  update: (id: number, patch: ScheduleEventPatch) =>
    api.patch<ScheduleEvent>(`/schedule/events/${id}`, patch).catch(explainOutage),
  remove: (id: number) => api.delete<void>(`/schedule/events/${id}`).catch(explainOutage),
};

/*
 * 404 на список — это не «событий нет» (тогда приходит пустой массив), а
 * отсутствие самого модуля на этом сервере: бэкенд старее планшета. Сказать
 * это словами, а не «Cannot GET /api/v1/schedule/events».
 */
async function listOrExplain(from: string, to: string): Promise<ScheduleEvent[]> {
  try {
    return await remote.list(from, to);
  } catch (err) {
    if (err instanceof NotFoundError)
      throw new ApiError("На этом сервере раздел «Мой график» ещё не развёрнут", 404, "not_found");
    throw err;
  }
}

/* ── Публичный интерфейс + оповещение о правках ─────────────────────── */

/*
 * Один и тот же день показывают два места — страница графика и полоска на
 * главной. После правки на странице полоска должна перечитать данные, а не
 * ждать перезагрузки, поэтому каждая запись оповещает подписчиков.
 */
const listeners = new Set<() => void>();

export function subscribeSchedule(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify(): void {
  for (const listener of listeners) listener();
}

export function fetchScheduleEvents(from: string, to: string): Promise<ScheduleEvent[]> {
  checkRange(from, to);
  return listOrExplain(from, to);
}

export async function createScheduleEvent(input: ScheduleEventInput): Promise<ScheduleEvent> {
  const event = await remote.create(input);
  notify();
  return event;
}

/*
 * 404 на правку и удаление — событие уже удалили, обычно с другого устройства
 * той же учётки. Список на экране при этом устарел: перечитываем его и говорим
 * по-человечески, а не «Событие графика с id 3 не найдено».
 */
function explainGone(err: unknown): never {
  if (err instanceof ApiError && err.status === 404) {
    notify();
    throw new ApiError("Событие уже удалено — возможно, с другого устройства. График обновлён.", 404, "not_found");
  }
  throw err;
}

export async function updateScheduleEvent(id: number, patch: ScheduleEventPatch): Promise<ScheduleEvent> {
  const event = await remote.update(id, patch).catch(explainGone);
  notify();
  return event;
}

export async function deleteScheduleEvent(id: number): Promise<void> {
  await remote.remove(id).catch(explainGone);
  notify();
}
